import { useState, useEffect, useRef, useCallback } from 'react';
import { useRedactionMode } from '../contexts/RedactionModeContext';
import { documentApi } from '../services/api';
import type { PDFDocumentProxy } from 'pdfjs-dist';

interface LineGeometry {
  text: string;
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface PiiRect {
  /** Normalised 0–1 x position on the page */
  x: number;
  /** Normalised 0–1 y position on the page */
  y: number;
  /** Normalised 0–1 width */
  width: number;
  /** Normalised 0–1 height */
  height: number;
}

/** Horizontal padding (fraction of line width) added to each side of a PII rect */
const H_PADDING = 0.02;

// ── Module-level caches (persist across component remounts within the session) ─
const geometryCache = new Map<string, LineGeometry[]>();
const noGeometryPages = new Set<string>();
const detectedPiiCache = new Map<string, string[]>();

function clearAllCaches() {
  geometryCache.clear();
  noGeometryPages.clear();
  detectedPiiCache.clear();
}

interface UsePdfRedactionOptions {
  documentId: string | null | undefined;
  visiblePages: Set<number>;
  totalPages: number;
  /** pdf.js document — used as fallback geometry source for text-layer PDFs */
  pdfDocument?: PDFDocumentProxy | null;
}

/**
 * Hook that detects PII in PDF pages by matching PII values against text geometry.
 *
 * PII sources (in priority order):
 * 1. Haiku-detected PII stored per document (from processing-time detection)
 * 2. Structured PII from case data (fallback for documents processed before Haiku detection)
 *
 * Geometry sources (in priority order):
 * 1. Stored Textract LINE geometry via documentApi.getLineGeometry()
 * 2. pdf.js text content extraction (fallback for text-layer PDFs)
 */
export function usePdfRedaction({ documentId, visiblePages, totalPages, pdfDocument }: UsePdfRedactionOptions) {
  const { isEnabled, sourceDocsRevealed, structuredPii } = useRedactionMode();

  const [piiRectsPerPage, setPiiRectsPerPage] = useState<Map<number, PiiRect[]>>(new Map());
  const [documentPii, setDocumentPii] = useState<Set<string>>(new Set());

  const prevDocRef = useRef<string | null | undefined>(documentId);
  const prevEnabledRef = useRef(isEnabled);

  // Handle document changes and redaction mode toggles
  useEffect(() => {
    const docChanged = prevDocRef.current !== documentId;
    const wasEnabled = prevEnabledRef.current;

    prevDocRef.current = documentId;
    prevEnabledRef.current = isEnabled;

    if (wasEnabled && !isEnabled) {
      clearAllCaches();
      setPiiRectsPerPage(new Map());
      setDocumentPii(new Set());
    } else if (docChanged && isEnabled) {
      setPiiRectsPerPage(new Map());
      setDocumentPii(new Set());
    }
  }, [documentId, isEnabled]);

  // Fetch Haiku-detected PII for the current document
  useEffect(() => {
    if (!documentId || !isEnabled || sourceDocsRevealed) return;

    // Check cache first
    const cached = detectedPiiCache.get(documentId);
    if (cached) {
      setDocumentPii(new Set(cached));
      return;
    }

    let cancelled = false;
    documentApi.getDetectedPii(documentId).then(pii => {
      if (cancelled) return;
      if (pii && pii.length > 0) {
        detectedPiiCache.set(documentId, pii);
        setDocumentPii(new Set(pii));
      }
    }).catch(() => {
      // Silently fall back to structured PII
    });

    return () => { cancelled = true; };
  }, [documentId, isEnabled, sourceDocsRevealed]);

  // Effective PII: prefer document-level detected PII, fall back to structured PII from case data
  const effectivePii = documentPii.size > 0 ? documentPii : structuredPii;

  const processPage = useCallback(async (pageNum: number) => {
    if (!documentId || !isEnabled || sourceDocsRevealed || effectivePii.size === 0) {
      return;
    }

    const cacheKey = `${documentId}:${pageNum}`;

    // Already known to have no geometry from any source
    if (noGeometryPages.has(cacheKey)) return;

    // Check module-level geometry cache first
    let lines = geometryCache.get(cacheKey);
    if (!lines) {
      // Try stored Textract geometry from the API
      try {
        const fetched = await documentApi.getLineGeometry(documentId, pageNum);
        if (fetched && fetched.length > 0) {
          lines = fetched;
          geometryCache.set(cacheKey, lines);
        }
      } catch {
        // API error — fall through to pdf.js fallback
      }

      // Fallback: extract text positions from pdf.js text layer
      if (!lines && pdfDocument) {
        try {
          const extracted = await extractPdfjsTextGeometry(pdfDocument, pageNum);
          if (extracted && extracted.length > 0) {
            lines = extracted;
            geometryCache.set(cacheKey, lines);
          }
        } catch {
          // pdf.js extraction failed
        }
      }

      // No geometry from any source
      if (!lines) {
        noGeometryPages.add(cacheKey);
        return;
      }
    }

    // Match PII against line text and compute rects
    const rects = matchPiiToRects(lines, effectivePii);

    if (rects.length > 0) {
      setPiiRectsPerPage(prev => {
        const next = new Map(prev);
        next.set(pageNum, rects);
        return next;
      });
    }
  }, [documentId, isEnabled, sourceDocsRevealed, effectivePii, pdfDocument]);

  // Process visible pages when they change
  useEffect(() => {
    if (!isEnabled || sourceDocsRevealed || !documentId || effectivePii.size === 0) return;

    for (const pageNum of visiblePages) {
      processPage(pageNum);
    }
  }, [visiblePages, isEnabled, sourceDocsRevealed, documentId, processPage, effectivePii]);

  // Recompute rects when PII set changes (e.g. detected PII loaded async)
  useEffect(() => {
    if (!isEnabled || sourceDocsRevealed || !documentId || effectivePii.size === 0) return;

    const newRects = new Map<number, PiiRect[]>();
    for (const [key, lines] of geometryCache) {
      if (!key.startsWith(`${documentId}:`)) continue;
      const pageNum = parseInt(key.split(':')[1], 10);
      const rects = matchPiiToRects(lines, effectivePii);
      if (rects.length > 0) {
        newRects.set(pageNum, rects);
      }
    }
    if (newRects.size > 0) {
      setPiiRectsPerPage(prev => {
        const merged = new Map(prev);
        for (const [page, rects] of newRects) {
          merged.set(page, rects);
        }
        return merged;
      });
    }
  }, [effectivePii, documentId, isEnabled, sourceDocsRevealed]);

  return { piiRectsPerPage };
}

/**
 * Extract text geometry from a pdf.js document page.
 *
 * pdf.js returns text as small fragments (individual spans), not whole lines.
 * We group adjacent fragments on the same y-position into logical lines so that
 * PII strings spanning multiple fragments (e.g. "Margaret Elizabeth Thompson"
 * split across spans) can be matched as a single unit.
 */
async function extractPdfjsTextGeometry(
  pdfDocument: PDFDocumentProxy,
  pageNum: number
): Promise<LineGeometry[]> {
  const page = await pdfDocument.getPage(pageNum);
  const viewport = page.getViewport({ scale: 1 });
  const textContent = await page.getTextContent();

  // Phase 1: Convert each text item to normalised coordinates
  interface NormItem {
    text: string;
    left: number;
    top: number;
    right: number;
    height: number;
    yPdf: number; // original PDF y for grouping
  }

  const items: NormItem[] = [];
  for (const item of textContent.items) {
    if (!('str' in item) || !item.str || item.str.trim().length === 0) continue;

    const tx = item.transform[4];
    const ty = item.transform[5];
    const itemHeight = ('height' in item && item.height > 0)
      ? item.height
      : Math.abs(item.transform[0]) || Math.abs(item.transform[3]);

    const left = tx / viewport.width;
    const top = 1 - ((ty + itemHeight) / viewport.height);
    const width = item.width / viewport.width;
    const height = itemHeight / viewport.height;

    items.push({
      text: item.str,
      left: Math.max(0, left),
      top: Math.max(0, top),
      right: Math.min(1, left + width),
      height: Math.max(0, height),
      yPdf: ty,
    });
  }

  if (items.length === 0) return [];

  // Phase 2: Group items into lines by y-position proximity
  // Items within ~3pt vertically are considered the same line
  const Y_TOLERANCE = 3 / viewport.height;
  items.sort((a, b) => a.top - b.top || a.left - b.left);

  const lines: LineGeometry[] = [];
  let lineItems: NormItem[] = [items[0]];

  for (let i = 1; i < items.length; i++) {
    const item = items[i];
    const prevItem = lineItems[0];

    if (Math.abs(item.top - prevItem.top) <= Y_TOLERANCE) {
      // Same line — append
      lineItems.push(item);
    } else {
      // New line — flush previous
      lines.push(mergeItemsToLine(lineItems));
      lineItems = [item];
    }
  }
  // Flush final line
  lines.push(mergeItemsToLine(lineItems));

  return lines;
}

/** Merge a group of text items (same y-position) into a single LineGeometry. */
function mergeItemsToLine(items: Array<{
  text: string; left: number; top: number; right: number; height: number;
}>): LineGeometry {
  // Sort left-to-right
  items.sort((a, b) => a.left - b.left);

  const text = items.map(it => it.text).join('');
  const left = items[0].left;
  const right = items[items.length - 1].right;
  const top = Math.min(...items.map(it => it.top));
  const height = Math.max(...items.map(it => it.height));

  return {
    text,
    left,
    top,
    width: right - left,
    height,
  };
}

/**
 * Scan each line's text for PII values and compute bounding box rects.
 * Uses case-insensitive substring matching with proportional sub-line positioning.
 */
function matchPiiToRects(
  lines: LineGeometry[],
  piiValues: Set<string>
): PiiRect[] {
  const rects: PiiRect[] = [];

  for (const line of lines) {
    const lowerLineText = line.text.toLowerCase();
    const lineTextLen = line.text.length;
    if (lineTextLen === 0) continue;

    for (const piiValue of piiValues) {
      const lowerPii = piiValue.toLowerCase();
      let searchFrom = 0;

      while (searchFrom < lowerLineText.length) {
        const idx = lowerLineText.indexOf(lowerPii, searchFrom);
        if (idx === -1) break;

        // Proportional sub-line positioning
        const startRatio = idx / lineTextLen;
        const endRatio = (idx + piiValue.length) / lineTextLen;

        const rectX = line.left + line.width * startRatio - H_PADDING;
        const rectWidth = line.width * (endRatio - startRatio) + H_PADDING * 2;

        rects.push({
          x: Math.max(0, rectX),
          y: Math.max(0, line.top),
          width: Math.min(1 - Math.max(0, rectX), rectWidth),
          height: line.height,
        });

        searchFrom = idx + 1;
      }
    }
  }

  return mergeOverlappingRects(rects);
}

/**
 * Merge rects that overlap horizontally on the same line to prevent
 * double-blurring when PII values are substrings of each other
 * (e.g. "Margaret Thompson" and "Margaret" both matched).
 */
function mergeOverlappingRects(rects: PiiRect[]): PiiRect[] {
  if (rects.length <= 1) return rects;

  // Sort by y then x so we can merge in a single pass per row
  const sorted = [...rects].sort((a, b) => a.y - b.y || a.x - b.x);
  const merged: PiiRect[] = [sorted[0]];

  for (let i = 1; i < sorted.length; i++) {
    const curr = sorted[i];
    const prev = merged[merged.length - 1];

    // Same row (overlapping y) and horizontally overlapping or adjacent?
    const sameRow = Math.abs(curr.y - prev.y) < 0.005;
    const overlapsX = curr.x <= prev.x + prev.width;

    if (sameRow && overlapsX) {
      // Extend the previous rect to cover both
      const newRight = Math.max(prev.x + prev.width, curr.x + curr.width);
      prev.width = newRight - prev.x;
      prev.height = Math.max(prev.height, curr.height);
    } else {
      merged.push(curr);
    }
  }

  return merged;
}
